import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
} from '@mui/material'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Banner,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  NotionButton,
  NotionCheckbox,
  NotionTextField,
  PageLoading,
  PageScaffold,
  SubsectionHeader,
} from '@/apps/latest/components/portal/notion'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import SoapTemplateSection from '@/apps/latest/components/portal/SoapTemplateSection'
import { SearchBox } from '@/apps/latest/components/portal/SearchBox'
import {
  useDrugGroupDetailQuery,
  useUpdateDrugGroupMutation,
  useDeleteDrugGroupMutation,
  useDrugGroupDrugs,
} from '@/features/internal/drugs'
import {
  useGroupSoapTemplatesQuery,
  useUpdateGroupSoapTemplatesMutation,
} from '@/features/internal/drugs'
import {
  SoapTemplateGroup,
  SoapType,
  Drug,
} from '@/features/internal/drugs/types/soapTemplate'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material'

/**
 * 医薬品グループ詳細・編集画面
 */
const DrugGroupDetailPage: React.FC = () => {
  // URLからグループIDを取得
  const { id: groupId = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // 'create'パスの場合のフラグ（hooksは常に呼び出す必要があるため、returnは後で行う）
  const isCreatePath = groupId === 'create'

  // 医薬品グループ詳細クエリ（TanStack Query）
  // create パスの場合は無効化
  const {
    data: selectedGroup,
    isLoading: groupLoading,
    error: groupError,
  } = useDrugGroupDetailQuery(isCreatePath ? undefined : groupId)

  // 更新・削除 Mutation
  const updateGroupMutation = useUpdateDrugGroupMutation()
  const deleteGroupMutation = useDeleteDrugGroupMutation()

  // create パスの場合は空のグループIDを渡す（hooksは常に呼び出す必要がある）
  const {
    loading: drugsLoading,
    error: drugsError,
    groupDrugs,
    selectedDrugIds,
    removeDrugFromGroupAction,
    bulkDeleteDrugsFromGroupAction,
    toggleDrugSelection,
    selectAllDrugs,
    deselectAllDrugs,
    clearSelection,
  } = useDrugGroupDrugs(isCreatePath ? '' : groupId)

  // テンプレート関連のフック（TanStack Query）
  // create パスの場合は無効化
  const {
    templateGroups,
    isLoading: templatesLoading,
    error: templatesError,
  } = useGroupSoapTemplatesQuery(isCreatePath ? undefined : groupId)

  // 更新 Mutation
  const updateTemplatesMutation = useUpdateGroupSoapTemplatesMutation()

  // 状態管理 (作成ダイアログからの遷移は ?edit=true で編集モード開始)
  const [editMode, setEditMode] = useState(
    () => searchParams.get('edit') === 'true'
  )
  const [groupName, setGroupName] = useState(() => selectedGroup?.name || '')
  const [groupDescription, setGroupDescription] = useState(
    () => selectedGroup?.description || ''
  )
  const [nameError, setNameError] = useState('')
  const [soapTemplates, setSoapTemplates] = useState<SoapTemplateGroup[]>(
    () => templateGroups || []
  )
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteSuccess, setDeleteSuccess] = useState(false)
  const [emptyItemsWarning, setEmptyItemsWarning] = useState<{
    show: boolean
    items: { groupName: string; soapType: string }[]
  }>({
    show: false,
    items: [],
  })
  const [unnamedGroupWarning, setUnnamedGroupWarning] = useState(false)
  const [drugSearchTerm, setDrugSearchTerm] = useState('')
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)
  const [removeDrugConfirmId, setRemoveDrugConfirmId] = useState<string | null>(
    null
  )

  // グループ詳細データが取得されたらフォームを初期化
  // 初期化は useState の lazy initialization で行うため、useEffect は不要

  // 所属医薬品はuseDrugGroupDrugs(groupId)でgroupId変更時に自動取得

  // テンプレートデータが取得されたら編集用状態を初期化
  // useEffectは削除 - useState の lazy initialization とキャンセルハンドラーで管理

  // 成功メッセージのタイムアウト管理
  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (saveSuccess) {
      timeoutId = setTimeout(() => {
        setSaveSuccess(false)
      }, 3000)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [saveSuccess])

  // 削除成功後のリダイレクト管理
  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (deleteSuccess) {
      timeoutId = setTimeout(() => {
        navigate('/portal/soaps')
      }, 3000)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [deleteSuccess, navigate])

  // 編集モード切替
  const toggleEditMode = () => {
    const newEditMode = !editMode
    setEditMode(newEditMode)
    // 編集モードを終了する場合は警告をクリア
    if (!newEditMode) {
      setEmptyItemsWarning({
        show: false,
        items: [],
      })
    }
  }

  // フォーム検証
  const validateForm = () => {
    let isValid = true

    if (!groupName.trim()) {
      setNameError('グループ名は必須です')
      isValid = false
    } else {
      setNameError('')
    }

    return isValid
  }

  // 空のSOAP要素をチェックする関数
  const validateEmptyItems = () => {
    const emptyItems: { groupName: string; soapType: string }[] = []

    // 全てのテンプレートグループをチェック
    soapTemplates.forEach((group) => {
      // 各グループ内のSOAP項目をチェック
      group.templates.forEach((item) => {
        // contentが空の場合は問題ありとマーク
        if (!item.content.trim()) {
          emptyItems.push({
            groupName: group.name,
            soapType: item.soap_type,
          })
        }
      })
    })

    return emptyItems
  }

  // 保存処理
  const handleSave = async () => {
    if (!validateForm()) return

    // グループ名未入力のテンプレートグループをチェック (backendはnameを必須とする)
    if (soapTemplates.some((group) => !group.name.trim())) {
      setUnnamedGroupWarning(true)
      setSaveSuccess(false)
      return
    }
    setUnnamedGroupWarning(false)

    // 空のSOAP要素をチェック
    const emptyItems = validateEmptyItems()

    // 空の要素がある場合は警告を表示して処理中断
    if (emptyItems.length > 0) {
      setEmptyItemsWarning({
        show: true,
        items: emptyItems,
      })
      // 成功メッセージが表示されないようにリセット
      setSaveSuccess(false)
      return
    }

    // 警告表示をクリア
    setEmptyItemsWarning({
      show: false,
      items: [],
    })

    try {
      // グループ更新（Mutation使用）
      await updateGroupMutation.mutateAsync({
        groupId,
        data: {
          name: groupName,
          description: groupDescription,
        },
      })

      // SOAPテンプレート更新（Mutation使用）
      await updateTemplatesMutation.mutateAsync({
        groupId,
        templateGroups: soapTemplates,
      })

      // 両方成功した場合のみ成功メッセージを表示
      setSaveSuccess(true)
      setEditMode(false)
    } catch (_err) {
      setSaveSuccess(false)
    }
  }

  // キャンセル処理 (= ConfirmDialog で確認)
  const handleCancel = () => {
    setCancelConfirmOpen(true)
  }

  const handleCancelConfirm = () => {
    if (selectedGroup) {
      setGroupName(selectedGroup.name)
      setGroupDescription(selectedGroup.description)
    }
    setSoapTemplates([...templateGroups])
    setEditMode(false)
    setEmptyItemsWarning({
      show: false,
      items: [],
    })
    setCancelConfirmOpen(false)
  }

  // 削除ダイアログを開く
  const openDeleteDialog = () => {
    setDeleteDialogOpen(true)
  }

  // 削除処理を実行（Mutation使用）
  const handleDelete = async () => {
    try {
      // 削除前にエラー状態をリセット
      setDeleteOperationError(false)
      await deleteGroupMutation.mutateAsync(groupId)

      setDeleteDialogOpen(false)
      setDeleteSuccess(true)

      // 削除成功後、3秒後に一覧画面へ戻る
      setTimeout(() => {
        navigate('/portal/soaps')
      }, 3000)
    } catch (_err) {
      setDeleteDialogOpen(false)
      setDeleteOperationError(true)
    }
  }

  // 削除ダイアログをキャンセル
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
  }

  // テンプレートグループ名変更ハンドラ
  const handleGroupNameChange = useCallback(
    (groupId: string, name: string) => {
      if (editMode) {
        setSoapTemplates((prevTemplates) =>
          prevTemplates.map((group) =>
            group.id === groupId ? { ...group, name } : group
          )
        )
      }
    },
    [editMode]
  )

  // SOAP項目追加ハンドラ
  const handleAddItem = useCallback(
    (groupId: string, soapType: SoapType) => {
      if (editMode) {
        setSoapTemplates((prevTemplates) =>
          prevTemplates.map((group) => {
            if (group.id === groupId) {
              return {
                ...group,
                templates: [
                  ...group.templates,
                  {
                    id: `new-item-${Date.now()}`,
                    soap_type: soapType,
                    content: '',
                  },
                ],
              }
            }
            return group
          })
        )
      }
    },
    [editMode]
  )

  // SOAP項目削除ハンドラ
  const handleDeleteItem = useCallback(
    (groupId: string, itemId: string) => {
      if (editMode) {
        setSoapTemplates((prevTemplates) =>
          prevTemplates.map((group) => {
            if (group.id === groupId) {
              return {
                ...group,
                templates: group.templates.filter((item) => item.id !== itemId),
              }
            }
            return group
          })
        )
      }
    },
    [editMode]
  )

  // キーワード変更ハンドラ
  const handleKeywordsChange = useCallback(
    (groupId: string, keywords: string[]) => {
      if (editMode) {
        setSoapTemplates((prevTemplates) =>
          prevTemplates.map((group) =>
            group.id === groupId ? { ...group, keywords } : group
          )
        )
      }
    },
    [editMode]
  )

  // SOAP項目内容変更ハンドラ
  const handleItemContentChange = useCallback(
    (groupId: string, itemId: string, content: string) => {
      if (editMode) {
        setSoapTemplates((prevTemplates) =>
          prevTemplates.map((group) => {
            if (group.id === groupId) {
              return {
                ...group,
                templates: group.templates.map((item) =>
                  item.id === itemId ? { ...item, content } : item
                ),
              }
            }
            return group
          })
        )
      }
    },
    [editMode]
  )

  // グループ削除ハンドラ
  const handleDeleteGroup = useCallback(
    (groupId: string) => {
      if (editMode) {
        setSoapTemplates((prevTemplates) =>
          prevTemplates.filter((group) => group.id !== groupId)
        )
      }
    },
    [editMode]
  )

  // 医薬品追加ページへの遷移
  const handleAddDrugs = () => {
    navigate(`/portal/soaps/groups/${groupId}/drugs`)
  }

  // 一覧へ戻る
  const handleBackToList = () => {
    navigate('/portal/soaps')
  }

  // ローディング状態（医薬品検索は displayData で管理）
  const isLoading =
    groupLoading ||
    templatesLoading ||
    updateGroupMutation.isPending ||
    deleteGroupMutation.isPending

  // エラーハンドリング用の状態変数（削除操作のエラーは別管理）
  const [deleteOperationError, setDeleteOperationError] =
    useState<boolean>(false)
  // 表示用エラー状態（医薬品検索エラーは displayData で管理）
  const hasError = groupError || templatesError

  // 所属する医薬品を削除するハンドラ (= ConfirmDialog で確認)
  const handleRemoveDrug = (drugId: string) => {
    setRemoveDrugConfirmId(drugId)
  }

  const handleRemoveDrugConfirm = async () => {
    const drugId = removeDrugConfirmId
    setRemoveDrugConfirmId(null)
    if (!drugId) return
    try {
      await removeDrugFromGroupAction(groupId, drugId)
    } catch (_err) {
      // エラーハンドリングは上位レイヤーで行われる
    }
  }

  // 選択された医薬品を一括削除
  const handleBulkDelete = async () => {
    setBulkDeleteDialogOpen(false)
    try {
      const success = await bulkDeleteDrugsFromGroupAction(
        groupId,
        selectedDrugIds
      )
      if (success) {
        clearSelection()
        setDrugSearchTerm('') // 検索をリセット
      }
    } catch (_err) {
      // エラーハンドリングは上位レイヤーで行われるため、ここでは何もしない
    }
  }

  // 表示データ（所属医薬品の部分一致フィルタ）
  const displayData = {
    drugs: !drugSearchTerm
      ? groupDrugs
      : groupDrugs.filter((drug) => {
          const searchLower = drugSearchTerm.toLowerCase()
          return (
            drug.yj_code.toLowerCase().includes(searchLower) ||
            (drug.product_name &&
              drug.product_name.toLowerCase().includes(searchLower)) ||
            (drug.general_name &&
              drug.general_name.toLowerCase().includes(searchLower))
          )
        }),
    loading: drugsLoading,
    error: drugsError,
    hasSearch: !!drugSearchTerm,
  }

  // すべて選択/解除の処理
  const isAllSelected =
    displayData.drugs.length > 0 &&
    displayData.drugs.every((drug) => selectedDrugIds.includes(drug.id))

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // displayData.drugs（GroupDrug[]）を Drug[] として扱うため型変換
      const drugsToSelect: Drug[] = displayData.drugs.map((drug) => ({
        ...drug,
        id: drug.id,
        yj_code: drug.yj_code,
        product_name: drug.product_name,
        base_product_name: drug.product_name || '',
        price: '',
        manufacturer: drug.manufacturer_name,
        general_name: drug.general_name || '',
        base_general_name: drug.general_name || '',
      }))
      selectAllDrugs(drugsToSelect)
    } else {
      const drugsToDeselect: Drug[] = displayData.drugs.map((drug) => ({
        ...drug,
        id: drug.id,
        yj_code: drug.yj_code,
        product_name: drug.product_name,
        base_product_name: drug.product_name || '',
        price: '',
        manufacturer: drug.manufacturer_name,
        general_name: drug.general_name || '',
        base_general_name: drug.general_name || '',
      }))
      deselectAllDrugs(drugsToDeselect)
    }
  }

  const drugTableColumns: DataTableColumn[] = useMemo(
    () => [
      ...(editMode ? [{ key: 'select', label: '' as React.ReactNode }] : []),
      { key: 'yj_code', label: 'YJコード' },
      { key: 'product_name', label: '医薬品名' },
      { key: 'manufacturer_name', label: '製造元' },
      ...(editMode
        ? [{ key: 'actions', label: '', align: 'right' as const }]
        : []),
    ],
    [editMode]
  )

  // 'create'パスの場合はレンダリングしない（create.tsxを優先する）
  // hooks は全て呼び出し済みなので、ここで早期リターンしても rules-of-hooks 違反にならない
  if (isCreatePath) {
    return null
  }

  return (
    <PageScaffold title={selectedGroup?.name || 'グループ詳細'}>
      <Box>
        {!editMode && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <NotionButton
              variant="secondary"
              startIcon={<EditIcon />}
              onClick={toggleEditMode}
            >
              編集
            </NotionButton>
          </Box>
        )}

        {isLoading && <PageLoading />}
        {saveSuccess && (
          <Box sx={{ mb: 3 }}>
            <Banner severity="success">
              医薬品グループが正常に更新されました。
            </Banner>
          </Box>
        )}
        {deleteSuccess && (
          <Box sx={{ mb: 3 }}>
            <Banner severity="info">
              医薬品グループが削除されました。一覧画面へ戻ります...
            </Banner>
          </Box>
        )}
        {hasError && !deleteSuccess && (
          <Box sx={{ mb: 3 }}>
            <Banner severity="danger">
              操作中にエラーが発生しました。しばらくしてから再度お試しください。
            </Banner>
          </Box>
        )}
        {deleteOperationError && (
          <Box sx={{ mb: 3 }}>
            <Banner severity="danger">
              医薬品グループの削除中にエラーが発生しました。再度お試しください。
            </Banner>
          </Box>
        )}

        {!isLoading && (
          <>
            {/* 基本情報フォーム/表示 */}
            <Box sx={{ mb: 4 }}>
              <SubsectionHeader heading="基本情報" />
              {editMode ? (
                <Stack gap={2}>
                  <NotionTextField
                    label="グループ名"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    error={!!nameError}
                    helperText={nameError}
                    required
                  />
                  <NotionTextField
                    label="説明"
                    value={groupDescription}
                    onChange={(e) => setGroupDescription(e.target.value)}
                    multiline
                    rows={3}
                  />
                </Stack>
              ) : (
                <Stack gap={1.5}>
                  <Box>
                    <Typography sx={PORTAL_TEXT.rowLabel}>
                      グループ名
                    </Typography>
                    <Typography sx={PORTAL_TEXT.cellBody}>
                      {groupName}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={PORTAL_TEXT.rowLabel}>説明</Typography>
                    <Typography sx={PORTAL_TEXT.cellBody}>
                      {groupDescription || '(なし)'}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Box>

            {/* 所属医薬品一覧 */}
            <Box sx={{ mb: 4 }}>
              <SubsectionHeader
                heading="所属医薬品"
                actions={
                  editMode ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {selectedDrugIds.length > 0 && (
                        <NotionButton
                          startIcon={<DeleteOutlineIcon />}
                          variant="destructive"
                          onClick={() => setBulkDeleteDialogOpen(true)}
                        >
                          選択した{selectedDrugIds.length}件を削除
                        </NotionButton>
                      )}
                      <NotionButton
                        startIcon={<AddIcon />}
                        variant="secondary"
                        onClick={handleAddDrugs}
                      >
                        医薬品追加
                      </NotionButton>
                    </Box>
                  ) : undefined
                }
              />
              {editMode && (
                <Box sx={{ mb: 2 }}>
                  <SearchBox
                    placeholder="医薬品名やYJコードで検索"
                    value={drugSearchTerm}
                    onChange={setDrugSearchTerm}
                    onSearch={() => undefined}
                    variant="standard"
                  />
                </Box>
              )}

              {displayData.loading && (
                <Skeleton variant="rectangular" height={48} sx={{ my: 2 }} />
              )}

              {displayData.error && (
                <Box sx={{ mb: 2 }}>
                  <Banner severity="danger">
                    医薬品検索中にエラーが発生しました。
                  </Banner>
                </Box>
              )}

              {/* 全て選択チェックボックス（編集モード時のみ） */}
              {editMode &&
                displayData.drugs &&
                displayData.drugs.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <NotionCheckbox
                      checked={isAllSelected}
                      indeterminate={
                        selectedDrugIds.length > 0 && !isAllSelected
                      }
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      label="全て選択"
                    />
                  </Box>
                )}

              <DataTable columns={drugTableColumns}>
                {displayData.drugs && displayData.drugs.length > 0 ? (
                  displayData.drugs.map((drug) => (
                    <TableRow key={drug.id}>
                      {editMode && (
                        <TableCell padding="checkbox">
                          <NotionCheckbox
                            checked={selectedDrugIds.includes(drug.id)}
                            onChange={() => toggleDrugSelection(drug.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>{drug.yj_code}</TableCell>
                      <TableCell>
                        {drug.product_name ? (
                          drug.product_name
                        ) : (
                          <Box
                            component="span"
                            sx={{ color: 'text.secondary' }}
                          >
                            【般】{drug.general_name}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{drug.manufacturer_name}</TableCell>
                      {editMode && (
                        <TableCell align="right">
                          <Tooltip title="削除" placement="top">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveDrug(drug.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={editMode ? 5 : 3}>
                      <Typography sx={PORTAL_TEXT.emptyHint}>
                        {displayData.hasSearch
                          ? '一致する医薬品がありません'
                          : '(なし)'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </DataTable>
            </Box>
            {/* SOAPテンプレート編集セクション */}
            <Box sx={{ mb: 4 }}>
              <SubsectionHeader
                heading="SOAPテンプレート"
                description="キーワードが会話にすべて含まれると、そのテンプレートがSOAPに挿入されます"
              />
              {soapTemplates.length === 0 && !editMode && (
                <Typography sx={PORTAL_TEXT.emptyHint}>(なし)</Typography>
              )}
              {soapTemplates.map((group) => (
                <SoapTemplateSection
                  key={group.id}
                  group={group}
                  editMode={editMode}
                  onGroupNameChange={handleGroupNameChange}
                  onKeywordsChange={handleKeywordsChange}
                  onAddItem={handleAddItem}
                  onDeleteItem={handleDeleteItem}
                  onItemContentChange={handleItemContentChange}
                  onDeleteGroup={handleDeleteGroup}
                />
              ))}
              {editMode && (
                <NotionButton
                  variant="ghost"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setSoapTemplates([
                      ...soapTemplates,
                      {
                        id: `new-template-group-${Date.now()}`,
                        name: '',
                        keywords: [],
                        templates: [],
                      },
                    ])
                  }}
                  sx={{ mt: 1 }}
                >
                  テンプレートグループを追加
                </NotionButton>
              )}
            </Box>

            {unnamedGroupWarning && (
              <Box sx={{ mb: 3 }}>
                <Banner severity="warn">
                  グループ名が未入力のテンプレートグループがあります
                </Banner>
              </Box>
            )}

            {emptyItemsWarning.show && (
              <Box sx={{ mb: 3 }}>
                <Banner severity="warn">
                  空のSOAP項目が{emptyItemsWarning.items.length}
                  件あります。内容を入力するか項目を削除してから保存してください
                </Banner>
              </Box>
            )}

            {/* アクションボタン */}
            {editMode ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: 4,
                }}
              >
                <NotionButton variant="destructive" onClick={openDeleteDialog}>
                  削除
                </NotionButton>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <NotionButton variant="secondary" onClick={handleCancel}>
                    キャンセル
                  </NotionButton>
                  <NotionButton
                    variant="primary"
                    onClick={handleSave}
                    disabled={isLoading}
                  >
                    {isLoading ? '保存中...' : '保存'}
                  </NotionButton>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 4,
                }}
              >
                <NotionButton variant="secondary" onClick={handleBackToList}>
                  一覧へ戻る
                </NotionButton>
              </Box>
            )}
          </>
        )}
      </Box>

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDelete}
        title="医薬品グループの削除"
        message={`「${groupName}」をSOAPテンプレートごと削除しますか？`}
        confirmLabel="削除する"
        variant="danger"
      />

      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        title="選択した医薬品の削除"
        message={`選択した${selectedDrugIds.length}件の医薬品をグループから削除しますか？`}
        confirmLabel="削除する"
        variant="danger"
      />

      <ConfirmDialog
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={handleCancelConfirm}
        title="編集内容の破棄"
        message="編集内容が破棄されますが、よろしいですか？"
        confirmLabel="破棄する"
        variant="danger"
      />

      <ConfirmDialog
        open={removeDrugConfirmId !== null}
        onClose={() => setRemoveDrugConfirmId(null)}
        onConfirm={handleRemoveDrugConfirm}
        title="医薬品の削除"
        message="この医薬品をグループから削除してよろしいですか？"
        confirmLabel="削除する"
        variant="danger"
      />
    </PageScaffold>
  )
}

export default DrugGroupDetailPage
